import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { I18nService } from 'i18n';
import { CommunityMessagesService } from '../../../services/community-messages.service';
import { CommunityMessageRecipient } from '../../../models/community-messages.models';
import { communityErrorKey } from '../../../models/community.models';
import { CommunityAvatarComponent } from '../../../components/community-avatar/community-avatar.component';

/**
 * Compose a private message (`community/messages/new?to=<id>`, Phase 3).
 *
 * Who may be picked is already the result of the community's communication
 * rules (`GET /messages/recipients`): a regular member gets the reachable
 * list outright, an admin gets a live search across everyone. Sending to
 * someone the rules closed since the list loaded still comes back 422 with
 * `errors.recipient`, shown as "recipient not reachable".
 */
@Component({
  selector: 'app-community-message-compose-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, TranslocoModule, CommunityAvatarComponent],
  templateUrl: './community-message-compose-page.component.html',
  styleUrl: './community-message-compose-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityMessageComposePageComponent implements OnInit {
  private messagesService = inject(CommunityMessagesService);
  private i18n = inject(I18nService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  isAdmin = signal(false);
  /** A network-wide community mute also closes private messages (backend 403 `community_muted`). */
  messagingMuted = signal(false);
  /** The fixed recipient list for a regular member; empty (and unused) for admins, who search instead. */
  recipients = signal<CommunityMessageRecipient[]>([]);
  /** Admin search results. */
  searchResults = signal<CommunityMessageRecipient[]>([]);
  searchTerm = '';

  recipient = signal<CommunityMessageRecipient | null>(null);
  recipientId = signal<number | null>(null);
  subject = '';
  content = '';

  loading = signal(true);
  sending = signal(false);
  error = signal<string | null>(null);
  loadError = signal<string | null>(null);

  private search$ = new Subject<string>();

  ngOnInit(): void {
    const to = this.route.snapshot.queryParamMap.get('to');
    if (to && /^\d+$/.test(to)) this.recipientId.set(Number(to));

    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(q =>
          q.trim().length < 2
            ? of({ recipients: [] as CommunityMessageRecipient[], is_admin: true })
            : this.messagesService.recipients(q).pipe(catchError(() => of({ recipients: [] as CommunityMessageRecipient[], is_admin: true })))
        )
      )
      .subscribe(res => this.searchResults.set(res.recipients));

    this.loading.set(true);
    this.messagesService.recipients().subscribe({
      next: res => {
        this.isAdmin.set(res.is_admin);
        this.messagingMuted.set(!!res.messaging_muted);
        this.recipients.set(res.recipients);
        this.loading.set(false);
        this.preselectRecipient(res.recipients, res.is_admin);
      },
      error: err => {
        this.loading.set(false);
        this.loadError.set(communityErrorKey(err));
      }
    });
  }

  /**
   * A `?to=` id is applied against the batch just loaded; for an admin that
   * batch is only the first 50 users by id, so a miss there falls back to the
   * id-search the admin recipient search already supports.
   */
  private preselectRecipient(batch: CommunityMessageRecipient[], isAdmin: boolean): void {
    const preselect = this.recipientId();
    if (preselect == null) return;
    const found = batch.find(r => r.id === preselect);
    if (found) {
      this.pick(found);
      return;
    }
    if (!isAdmin) return;
    this.messagesService.recipients(String(preselect)).subscribe(res => {
      const match = res.recipients.find(r => r.id === preselect);
      if (match) this.pick(match);
    });
  }

  get lang(): string {
    return this.i18n.getLanguage();
  }

  backLink(): string {
    return `/${this.lang}/community/messages`;
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.recipient.set(null);
    this.recipientId.set(null);
    this.search$.next(term);
  }

  selectRecipient(value: string | number | null): void {
    const id = value == null || value === '' ? null : Number(value);
    this.recipientId.set(id);
    this.recipient.set(id != null ? this.recipients().find(r => r.id === id) ?? null : null);
  }

  pick(r: CommunityMessageRecipient): void {
    this.recipient.set(r);
    this.recipientId.set(r.id);
    this.searchTerm = r.display_label;
    this.searchResults.set([]);
  }

  canSend(): boolean {
    return this.recipientId() != null && this.subject.trim().length > 0 && this.content.trim().length > 0;
  }

  send(): void {
    const recipientId = this.recipientId();
    if (!recipientId || !this.canSend()) return;
    this.sending.set(true);
    this.error.set(null);
    this.messagesService.send({ recipient_id: recipientId, subject: this.subject.trim(), content: this.content.trim() }).subscribe({
      next: res => {
        this.sending.set(false);
        this.router.navigate([`/${this.lang}/community/messages/${res.message.id}`]);
      },
      error: err => {
        this.sending.set(false);
        this.error.set(
          CommunityMessagesService.isRecipientUnreachable(err) ? 'community.messages.errors.recipient_unreachable' : communityErrorKey(err)
        );
      }
    });
  }
}
